import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';

const FarmMap = ({ language, selectedFarm, filters }) => {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const farmPolygons = [
    {
      id: 1,
      name: { en: "Yirgacheffe Highland Farm", zh: "耶加雪菲高地农场" },
      country: { en: "Ethiopia", zh: "埃塞俄比亚" },
      coordinates: [
        [38.18, 6.18], [38.22, 6.18], [38.22, 6.22], [38.18, 6.22], [38.18, 6.18]
      ],
      center: [38.2, 6.2],
      altitude: 2100,
      color: '#2F7A4A'
    },
    {
      id: 2,
      name: { en: "Sidamo Valley Estate", zh: "西达摩谷庄园" },
      country: { en: "Ethiopia", zh: "埃塞俄比亚" },
      coordinates: [
        [38.48, 6.48], [38.52, 6.48], [38.52, 6.52], [38.48, 6.52], [38.48, 6.48]
      ],
      center: [38.5, 6.5],
      altitude: 1950,
      color: '#2F7A4A'
    },
    {
      id: 3,
      name: { en: "Mount Elgon Coffee Farm", zh: "埃尔贡山咖啡农场" },
      country: { en: "Uganda", zh: "乌干达" },
      coordinates: [
        [34.48, 1.18], [34.52, 1.18], [34.52, 1.22], [34.48, 1.22], [34.48, 1.18]
      ],
      center: [34.5, 1.2],
      altitude: 1800,
      color: '#8B4513'
    },
    {
      id: 4,
      name: { en: "Rwenzori Mountain Farm", zh: "鲁文佐里山农场" },
      country: { en: "Uganda", zh: "乌干达" },
      coordinates: [
        [29.98, 0.48], [30.02, 0.48], [30.02, 0.52], [29.98, 0.52], [29.98, 0.48]
      ],
      center: [30.0, 0.5],
      altitude: 1650,
      color: '#8B4513'
    },
    {
      id: 5,
      name: { en: "Nyeri Highlands Estate", zh: "涅里高地庄园" },
      country: { en: "Kenya", zh: "肯尼亚" },
      coordinates: [
        [36.98, -0.42], [37.02, -0.42], [37.02, -0.38], [36.98, -0.38], [36.98, -0.42]
      ],
      center: [37.0, -0.4],
      altitude: 1750,
      color: '#D4A574'
    },
    {
      id: 6,
      name: { en: "Kiambu Coffee Cooperative", zh: "基安布咖啡合作社" },
      country: { en: "Kenya", zh: "肯尼亚" },
      coordinates: [
        [36.78, -1.22], [36.82, -1.22], [36.82, -1.18], [36.78, -1.18], [36.78, -1.22]
      ],
      center: [36.8, -1.2],
      altitude: 1600,
      color: '#D4A574'
    },
    {
      id: 7,
      name: { en: "Plateau State Farm", zh: "高原州农场" },
      country: { en: "Nigeria", zh: "尼日利亚" },
      coordinates: [
        [8.98, 9.48], [9.02, 9.48], [9.02, 9.52], [8.98, 9.52], [8.98, 9.48]
      ],
      center: [9.0, 9.5],
      altitude: 1400,
      color: '#22C55E'
    },
    {
      id: 8,
      name: { en: "Mambilla Plateau Estate", zh: "曼比拉高原庄园" },
      country: { en: "Nigeria", zh: "尼日利亚" },
      coordinates: [
        [10.98, 6.98], [11.02, 6.98], [11.02, 7.02], [10.98, 7.02], [10.98, 6.98]
      ],
      center: [11.0, 7.0],
      altitude: 1550,
      color: '#22C55E'
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => setMapLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (selectedFarm && mapRef?.current) {
      const farm = farmPolygons?.find(f => f?.id === selectedFarm?.id);
      if (farm) {
        mapRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedFarm]);

  const getMapUrl = () => {
    let center = [5.0, 20.0];
    let zoom = 4;

    if (selectedFarm) {
      const farm = farmPolygons?.find(f => f?.id === selectedFarm?.id);
      if (farm) {
        center = farm?.center;
        zoom = 12;
      }
    }

    return `https://www.google.com/maps?q=${center?.[1]},${center?.[0]}&z=${zoom}&output=embed`;
  };

  return (
    <div className="relative w-full h-full bg-muted" ref={mapRef}>
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <div className="text-center">
            <Icon name="Loader" size={40} className="animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {language === 'en' ? 'Loading map...' : '加载地图中...'}
            </p>
          </div>
        </div>
      )}
      <iframe
        src={getMapUrl()}
        width="100%"
        height="100%"
        loading="lazy"
        title={language === 'en' ? 'African Coffee Farms Map' : '非洲咖啡农场地图'}
        referrerPolicy="no-referrer-when-downgrade"
        className={`border-0 ${mapLoaded ? 'opacity-100' : 'opacity-0'} transition-smooth`}
      />
      <div className="absolute top-4 left-4 bg-card border border-border rounded-lg shadow-card p-3 max-w-xs">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Info" size={16} className="text-primary" />
          <span className="text-sm font-medium text-foreground">
            {language === 'en' ? 'Map Legend' : '地图图例'}
          </span>
        </div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#2F7A4A' }} />
            <span className="text-muted-foreground">
              {language === 'en' ? 'Ethiopia' : '埃塞俄比亚'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8B4513' }} />
            <span className="text-muted-foreground">
              {language === 'en' ? 'Uganda' : '乌干达'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#D4A574' }} />
            <span className="text-muted-foreground">
              {language === 'en' ? 'Kenya' : '肯尼亚'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22C55E' }} />
            <span className="text-muted-foreground">
              {language === 'en' ? 'Nigeria' : '尼日利亚'}
            </span>
          </div>
        </div>
      </div>
      {selectedFarm && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg shadow-modal p-4 max-w-sm w-full mx-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-heading font-semibold text-foreground">
              {selectedFarm?.name?.[language]}
            </h3>
            <div className="flex items-center gap-1 text-sm text-primary">
              <Icon name="Mountain" size={16} />
              <span>{selectedFarm?.altitude}m</span>
            </div>
          </div>
          
          <div className="space-y-1 text-sm text-muted-foreground mb-3">
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={14} />
              <span>{selectedFarm?.country?.[language]}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Building2" size={14} />
              <span>{selectedFarm?.supplier?.[language]}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Maximize2" size={14} />
              <span>{selectedFarm?.area}</span>
            </div>
          </div>

          {selectedFarm?.certifications && selectedFarm?.certifications?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedFarm?.certifications?.map((cert, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-success/10 text-success text-xs rounded-full"
                >
                  {cert}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FarmMap;